// src/pages/Admin/Cities/AdminStreetSingle.jsx
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api from "@/utils/axios";
import VideoUpload from "@/components/video/VideoUpload/VideoUpload";
import VideoPlayer from "@/components/video/VideoPlayer/VideoPlayer";
import cls from "./AdminStreetSingle.module.scss";

const STATUSES = ["open", "closed"];

const languageLabel = (code) => {
  const map = {
    en: "English",
    si: "සිංහල (Sinhala)",
    ta: "தமிழ் (Tamil)",
    hi: "हिन्दी (Hindi)",
  };
  return map[code] || code?.toUpperCase() || "";
};

const AdminStreetSingle = () => {
  const { cityId, streetId } = useParams();
  const navigate = useNavigate();

  const [street, setStreet] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadingKey, setUploadingKey] = useState("");

  const [form, setForm] = useState({
    streetName: "",
    streetStatus: "open",
    streetVibe: "",
    streetIntro: "",
    streetDescription: "",
    sortOrder: 0,
    isFeatured: false,
    tags: "",
    streetVideoId: "",
  });

  const [err, setErr] = useState("");
  const [saveMsg, setSaveMsg] = useState("");
  const [uploadMsg, setUploadMsg] = useState("");
  const [uploadErr, setUploadErr] = useState("");

  const [imageFiles, setImageFiles] = useState({
    avatarThumb: null,
    cover: null,
    mrNoty: null,
    mrsNoty: null,
  });

  const [imagePreviews, setImagePreviews] = useState({
    avatarThumb: null,
    cover: null,
    mrNoty: null,
    mrsNoty: null,
  });

  const token = useMemo(() => {
    if (typeof window === "undefined") return "";
    return localStorage.getItem("cm_at") || "";
  }, []);

  const hydrateForm = (doc) => {
    setForm({
      streetName: doc.streetName || "",
      streetStatus: doc.streetStatus || "open",
      streetVibe: doc.streetVibe || "",
      streetIntro: doc.streetIntro || "",
      streetDescription: doc.streetDescription || "",
      sortOrder: typeof doc.sortOrder === "number" ? doc.sortOrder : 0,
      isFeatured: Boolean(doc.isFeatured),
      tags: Array.isArray(doc.tags) ? doc.tags.join(", ") : "",
      streetVideoId: doc.streetVideoId?._id || doc.streetVideoId || "",
    });
  };

  const fetchStreet = useCallback(async () => {
    if (!cityId || !streetId) return;
    try {
      setLoading(true);
      setErr("");
      const { data } = await api.get(
        `/v1/admin/cities/${cityId}/streets/${streetId}`,
      );
      setStreet(data || null);
      if (data) hydrateForm(data);

      // reset previews/files when refreshing
      setImagePreviews({
        avatarThumb: null,
        cover: null,
        mrNoty: null,
        mrsNoty: null,
      });
      setImageFiles({
        avatarThumb: null,
        cover: null,
        mrNoty: null,
        mrsNoty: null,
      });
    } catch (e) {
      console.error("Load street failed", e);
      const msg =
        e?.response?.data?.message || e?.message || "Could not load street.";
      setErr(msg);
    } finally {
      setLoading(false);
    }
  }, [cityId, streetId]);

  useEffect(() => {
    fetchStreet();
  }, [fetchStreet]);

  const setField = (field, value) => {
    setForm((f) => ({ ...f, [field]: value }));
  };

  const handleBack = () => {
    navigate(-1);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!cityId || !streetId) return;
    setSaving(true);
    setSaveMsg("");
    setErr("");
    try {
      const payload = {
        ...form,
        sortOrder: Number(form.sortOrder) || 0,
        tags: form.tags
          .split(",")
          .map((t) => t.trim())
          .filter(Boolean),
      };
      const { data } = await api.patch(
        `/v1/admin/cities/${cityId}/streets/${streetId}`,
        payload,
      );
      setStreet(data || null);
      if (data) hydrateForm(data);
      setSaveMsg("Street details saved.");
    } catch (e) {
      console.error("Update street failed", e);
      const msg =
        e?.response?.data?.message || e?.message || "Failed to update street.";
      setErr(msg);
    } finally {
      setSaving(false);
    }
  };

  const handleFileChange = (key, file) => {
    setImageFiles((prev) => ({ ...prev, [key]: file || null }));
    if (file) {
      const url = URL.createObjectURL(file);
      setImagePreviews((prev) => ({ ...prev, [key]: url }));
    } else {
      setImagePreviews((prev) => ({ ...prev, [key]: null }));
    }
  };

  const doUpload = async (key, path) => {
    setUploadMsg("");
    setUploadErr("");
    const file = imageFiles[key];
    if (!file) {
      setUploadErr("Pick a file first.");
      return;
    }
    try {
      setUploadingKey(key);
      const ext = (file.name.split(".").pop() || "jpg").toLowerCase();
      const contentType = file.type || "application/octet-stream";

      const { data } = await api.post(
        `/v1/admin/cities/${cityId}/streets/${streetId}/presign/${path}`,
        { ext, contentType },
      );

      const { uploadUrl, processedKey } = data || {};
      if (!uploadUrl) throw new Error("Invalid presign response");

      const putRes = await fetch(uploadUrl, {
        method: "PUT",
        headers: { "Content-Type": contentType },
        body: file,
      });

      if (!putRes.ok) {
        console.error("S3 upload failed", putRes.status, await putRes.text());
        throw new Error("Failed to upload to storage.");
      }

      setUploadMsg(
        `${key} uploaded. Processed key saved as ${
          processedKey || "(pending)"
        }.`,
      );

      await fetchStreet();
    } catch (e) {
      console.error("Upload failed", e);
      const msg = e?.response?.data?.message || e?.message || "Upload failed.";
      setUploadErr(msg);
    } finally {
      setUploadingKey("");
    }
  };

  const attachVideoId = async (videoId) => {
    if (!videoId) {
      setUploadErr("Video ID is required to attach.");
      return;
    }
    try {
      setUploadErr("");
      await api.patch(`/v1/admin/cities/${cityId}/streets/${streetId}/video`, {
        videoId,
      });
      await fetchStreet();
      setUploadMsg(`Video ${videoId} attached.`);
    } catch (e) {
      console.error("Attach video failed", e);
      const msg =
        e?.response?.data?.message || e?.message || "Could not attach video.";
      setUploadErr(msg);
    }
  };

  const detachVideo = async () => {
    try {
      setUploadErr("");
      await api.patch(`/v1/admin/cities/${cityId}/streets/${streetId}`, {
        streetVideoId: null,
      });
      await fetchStreet();
      setUploadMsg("Video removed.");
    } catch (e) {
      const msg =
        e?.response?.data?.message || e?.message || "Could not remove video.";
      setUploadErr(msg);
    }
  };

  // Main avatar preview (use thumb if exists)
  const mainAvatarUrl = useMemo(() => {
    if (imagePreviews.avatarThumb) return imagePreviews.avatarThumb;
    if (street?.avatarThumb?.url) return street.avatarThumb.url;
    return null;
  }, [imagePreviews, street]);

  const slotUrl = (slot) => {
    if (imagePreviews[slot]) return imagePreviews[slot];
    if (slot === "avatarThumb") return street?.avatarThumb?.url || null;
    if (slot === "cover") return street?.cover?.url || null;
    if (slot === "mrNoty") return street?.mrNoty?.url || null;
    if (slot === "mrsNoty") return street?.mrsNoty?.url || null;
    return null;
  };

  if (loading && !street) {
    return (
      <div className={cls.page}>
        <div className={cls.header}>
          <button type="button" className={cls.backBtn} onClick={handleBack}>
            ← Back
          </button>
          <h1 className={cls.title}>Street</h1>
          <p className={cls.subtitle}>Loading street details…</p>
        </div>
        <div className={cls.skeleton} />
      </div>
    );
  }

  if (!street) {
    return (
      <div className={cls.page}>
        <div className={cls.header}>
          <button type="button" className={cls.backBtn} onClick={handleBack}>
            ← Back
          </button>
          <h1 className={cls.title}>Street</h1>
          <p className={cls.subtitle}>No street found for this ID.</p>
        </div>
      </div>
    );
  }

  return (
    <div className={cls.page}>
      {/* HEADER */}
      <div className={cls.header}>
        <button type="button" className={cls.backBtn} onClick={handleBack}>
          ← Back
        </button>
        <h1 className={cls.title}>Street Settings</h1>
        <p className={cls.subtitle}>
          Admin view to review and update street details, images, and video.
        </p>
      </div>

      {/* META BAR */}
      <div className={cls.metaBar}>
        <div className={cls.metaItem}>
          <span className={cls.metaLabel}>Street ID</span>
          <span className={cls.metaValue}>{street._id}</span>
        </div>
        <div className={cls.metaItem}>
          <span className={cls.metaLabel}>Language</span>
          <span className={cls.metaValue}>
            {languageLabel(street.streetLanguage)}
          </span>
        </div>
        <div className={cls.metaItem}>
          <span className={cls.metaLabel}>Status</span>
          <span className={cls.metaValue}>{street.streetStatus || "open"}</span>
        </div>
        <div className={cls.metaItem}>
          <span className={cls.metaLabel}>Created</span>
          <span className={cls.metaValue}>
            {street.createdAt
              ? new Date(street.createdAt).toLocaleString()
              : "—"}
          </span>
        </div>
      </div>

      {err && <div className={cls.error}>⚠ {err}</div>}
      {saveMsg && <div className={cls.success}>{saveMsg}</div>}
      {uploadMsg && <div className={cls.success}>{uploadMsg}</div>}
      {uploadErr && <div className={cls.error}>{uploadErr}</div>}

      <div className={cls.layout}>
        {/* LEFT COLUMN – IMAGES */}
        <section className={cls.avatarCard}>
          <div className={cls.avatarHeader}>
            <h2 className={cls.cardTitle}>Street Images</h2>
            <p className={cls.cardSubtitle}>
              Preview and upload the avatar thumb, cover, and Noty art.
            </p>
          </div>

          <div className={cls.avatarPreviewWrap}>
            <div className={cls.avatarGlow}>
              {mainAvatarUrl ? (
                <img
                  src={mainAvatarUrl}
                  alt="Street avatar"
                  className={cls.avatarImage}
                />
              ) : (
                <div className={cls.avatarFallback}>No avatar</div>
              )}
            </div>
            {street.streetAvatarUpdatedAt && (
              <p className={cls.avatarMeta}>
                Avatar updated{" "}
                {new Date(street.streetAvatarUpdatedAt).toLocaleString()}
              </p>
            )}
          </div>

          {/* IMAGE SLOTS */}
          <div className={cls.imagesGrid}>
            {/* Avatar Thumb */}
            <div className={cls.imageSlot}>
              <p className={cls.imageLabel}>Avatar Thumb (480.webp)</p>
              <div className={cls.imagePreviewMini}>
                {slotUrl("avatarThumb") ? (
                  <img
                    src={slotUrl("avatarThumb")}
                    alt="Avatar thumb"
                    className={cls.miniImage}
                  />
                ) : (
                  <div className={cls.miniFallback}>No thumb</div>
                )}
              </div>
              <p className={cls.imageHint}>
                Used for small spots like cards & lists.
              </p>
              <label className={cls.fileLabel}>
                <span className={cls.fileLabelText}>Choose thumb</span>
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) =>
                    handleFileChange("avatarThumb", e.target.files?.[0] || null)
                  }
                />
              </label>
              {imageFiles.avatarThumb && (
                <p className={cls.fileName}>
                  Selected: <strong>{imageFiles.avatarThumb.name}</strong>
                </p>
              )}
              <button
                type="button"
                className={cls.avatarBtn}
                onClick={() => doUpload("avatarThumb", "avatar-thumb")}
                disabled={
                  uploadingKey === "avatarThumb" || !imageFiles.avatarThumb
                }
              >
                {uploadingKey === "avatarThumb" ? "Uploading…" : "Upload Thumb"}
              </button>
            </div>

            {/* Cover */}
            <div className={cls.imageSlot}>
              <p className={cls.imageLabel}>Cover Image</p>
              <div className={cls.imagePreviewMini}>
                {slotUrl("cover") ? (
                  <img
                    src={slotUrl("cover")}
                    alt="Cover"
                    className={cls.miniImage}
                  />
                ) : (
                  <div className={cls.miniFallback}>No cover</div>
                )}
              </div>
              <p className={cls.imageHint}>
                Large hero banner when members enter this street.
              </p>
              <label className={cls.fileLabel}>
                <span className={cls.fileLabelText}>Choose cover</span>
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) =>
                    handleFileChange("cover", e.target.files?.[0] || null)
                  }
                />
              </label>
              {imageFiles.cover && (
                <p className={cls.fileName}>
                  Selected: <strong>{imageFiles.cover.name}</strong>
                </p>
              )}
              <button
                type="button"
                className={cls.avatarBtn}
                onClick={() => doUpload("cover", "cover")}
                disabled={uploadingKey === "cover" || !imageFiles.cover}
              >
                {uploadingKey === "cover" ? "Uploading…" : "Upload Cover"}
              </button>
            </div>

            {/* Mr Noty */}
            <div className={cls.imageSlot}>
              <p className={cls.imageLabel}>Mr Noty Image</p>
              <div className={cls.imagePreviewMini}>
                {slotUrl("mrNoty") ? (
                  <img
                    src={slotUrl("mrNoty")}
                    alt="Mr Noty"
                    className={cls.miniImage}
                  />
                ) : (
                  <div className={cls.miniFallback}>No Mr Noty</div>
                )}
              </div>
              <p className={cls.imageHint}>
                Character art for the street&apos;s male persona.
              </p>
              <label className={cls.fileLabel}>
                <span className={cls.fileLabelText}>Choose Mr Noty</span>
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) =>
                    handleFileChange("mrNoty", e.target.files?.[0] || null)
                  }
                />
              </label>
              {imageFiles.mrNoty && (
                <p className={cls.fileName}>
                  Selected: <strong>{imageFiles.mrNoty.name}</strong>
                </p>
              )}
              <button
                type="button"
                className={cls.avatarBtn}
                onClick={() => doUpload("mrNoty", "mr-noty")}
                disabled={uploadingKey === "mrNoty" || !imageFiles.mrNoty}
              >
                {uploadingKey === "mrNoty" ? "Uploading…" : "Upload Mr Noty"}
              </button>
            </div>

            {/* Mrs Noty */}
            <div className={cls.imageSlot}>
              <p className={cls.imageLabel}>Mrs Noty Image</p>
              <div className={cls.imagePreviewMini}>
                {slotUrl("mrsNoty") ? (
                  <img
                    src={slotUrl("mrsNoty")}
                    alt="Mrs Noty"
                    className={cls.miniImage}
                  />
                ) : (
                  <div className={cls.miniFallback}>No Mrs Noty</div>
                )}
              </div>
              <p className={cls.imageHint}>
                Character art for the street&apos;s female persona.
              </p>
              <label className={cls.fileLabel}>
                <span className={cls.fileLabelText}>Choose Mrs Noty</span>
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) =>
                    handleFileChange("mrsNoty", e.target.files?.[0] || null)
                  }
                />
              </label>
              {imageFiles.mrsNoty && (
                <p className={cls.fileName}>
                  Selected: <strong>{imageFiles.mrsNoty.name}</strong>
                </p>
              )}
              <button
                type="button"
                className={cls.avatarBtn}
                onClick={() => doUpload("mrsNoty", "mrs-noty")}
                disabled={uploadingKey === "mrsNoty" || !imageFiles.mrsNoty}
              >
                {uploadingKey === "mrsNoty" ? "Uploading…" : "Upload Mrs Noty"}
              </button>
            </div>
          </div>
        </section>

        {/* RIGHT COLUMN – STREET FORM */}
        <section className={cls.formCard}>
          <div className={cls.formHeader}>
            <h2 className={cls.cardTitle}>Street Details</h2>
            <p className={cls.cardSubtitle}>
              Name, status, vibe, intro, description, and flags for this street.
            </p>
          </div>

          <form className={cls.form} onSubmit={handleSave}>
            <div className={cls.formRow}>
              <div className={cls.field}>
                <label className={cls.label} htmlFor="streetName">
                  Street name
                </label>
                <input
                  id="streetName"
                  type="text"
                  className={cls.input}
                  value={form.streetName}
                  onChange={(e) => setField("streetName", e.target.value)}
                  placeholder="Neon Alley"
                />
              </div>

              <div className={cls.field}>
                <label className={cls.label} htmlFor="streetStatus">
                  Status
                </label>
                <select
                  id="streetStatus"
                  className={cls.select}
                  value={form.streetStatus}
                  onChange={(e) => setField("streetStatus", e.target.value)}
                >
                  {STATUSES.map((s) => (
                    <option key={s} value={s}>
                      {s}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className={cls.formRow}>
              <div className={cls.field}>
                <label className={cls.label} htmlFor="sortOrder">
                  Sort order
                </label>
                <input
                  id="sortOrder"
                  type="number"
                  className={cls.input}
                  value={form.sortOrder}
                  onChange={(e) => setField("sortOrder", e.target.value)}
                />
                <p className={cls.help}>
                  Lower numbers appear earlier in lists.
                </p>
              </div>

              <div className={cls.field}>
                <label className={cls.label} htmlFor="isFeatured">
                  Featured street
                </label>
                <div className={cls.checkboxRow}>
                  <input
                    id="isFeatured"
                    type="checkbox"
                    checked={form.isFeatured}
                    onChange={(e) => setField("isFeatured", e.target.checked)}
                  />
                  <span className={cls.help}>
                    Show in featured carousels and hero rows inside the city.
                  </span>
                </div>
              </div>
            </div>

            <div className={cls.field}>
              <label className={cls.label} htmlFor="streetVibe">
                Vibe / tagline
              </label>
              <input
                id="streetVibe"
                type="text"
                className={cls.input}
                value={form.streetVibe}
                onChange={(e) => setField("streetVibe", e.target.value)}
                placeholder="Where neon energy never sleeps…"
              />
            </div>

            <div className={cls.field}>
              <label className={cls.label} htmlFor="streetIntro">
                Intro
              </label>
              <textarea
                id="streetIntro"
                rows={3}
                className={cls.input}
                value={form.streetIntro}
                onChange={(e) => setField("streetIntro", e.target.value)}
                placeholder="Short intro shown on the first fold."
              />
            </div>

            <div className={cls.field}>
              <label className={cls.label} htmlFor="streetDescription">
                Description
              </label>
              <textarea
                id="streetDescription"
                rows={4}
                className={cls.input}
                value={form.streetDescription}
                onChange={(e) => setField("streetDescription", e.target.value)}
                placeholder="Longer description when someone opens the street."
              />
            </div>

            <div className={cls.field}>
              <label className={cls.label} htmlFor="tags">
                Tags (comma separated)
              </label>
              <input
                id="tags"
                type="text"
                className={cls.input}
                value={form.tags}
                onChange={(e) => setField("tags", e.target.value)}
                placeholder="neon, nightlife, music"
              />
            </div>

            <div className={cls.actions}>
              <button
                type="button"
                className={cls.secondaryBtn}
                onClick={fetchStreet}
                disabled={saving}
              >
                Reset / Refresh
              </button>
              <button
                type="submit"
                className={cls.primaryBtn}
                disabled={saving}
              >
                {saving ? "Saving…" : "Save changes"}
              </button>
            </div>
          </form>
        </section>
      </div>

      {/* VIDEO CARD */}
      <section className={cls.formCard}>
        <div className={cls.formHeader}>
          <h2 className={cls.cardTitle}>Street Video</h2>
          <p className={cls.cardSubtitle}>
            Attach an existing video, or upload a fresh one for this street.
          </p>
        </div>

        <div className={cls.videoRow}>
          <div className={cls.field}>
            <label className={cls.label} htmlFor="streetVideoId">
              Current video ID
            </label>
            <input
              id="streetVideoId"
              type="text"
              className={cls.input}
              value={form.streetVideoId}
              onChange={(e) => setField("streetVideoId", e.target.value)}
              placeholder="Video ObjectId"
            />
            <div className={cls.actions}>
              <button
                type="button"
                className={cls.primaryBtn}
                onClick={() => attachVideoId(form.streetVideoId)}
              >
                Attach Video
              </button>
              <button
                type="button"
                className={cls.secondaryBtn}
                onClick={detachVideo}
              >
                Remove Video
              </button>
            </div>
          </div>

          <div className={cls.preview}>
            {form.streetVideoId ? (
              <VideoPlayer
                token={token}
                videoId={form.streetVideoId}
                mode="proxy"
                height={220}
              />
            ) : (
              <div className={cls.noVideo}>No video attached.</div>
            )}
          </div>
        </div>

        <div className={cls.uploadBlock}>
          <VideoUpload
            defaultLang={street.streetLanguage || "en"}
            onDone={(res) => attachVideoId(res?.videoId)}
            showUploader
          />
        </div>
      </section>
    </div>
  );
};

export default AdminStreetSingle;
